import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { LiveBackground } from "./ClownNose";
import { colorMap } from "@/data/articulos";

export const relacionadas = (todas, actual, n = 3) => {
  const otras = todas.filter((x) => x.slug !== actual.slug);
  const mismas = otras.filter((x) => x.categoria === actual.categoria);
  const resto = otras.filter((x) => x.categoria !== actual.categoria);
  return [...mismas, ...resto].slice(0, n);
};

export const NotasRelacionadas = ({ todas, actual, base = "/blog", utop = false }) => {
  const items = relacionadas(todas, actual);
  if (!items.length) return null;
  if (utop) {
    return (
      <section className="relative overflow-hidden bg-utop-azul text-white" data-testid="notas-relacionadas">
        <div className="absolute inset-0 utop-pattern opacity-[0.07]" aria-hidden="true" />
        <UtopBlobs />
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
          <p className="font-utophand text-utop-mostaza text-2xl md:text-3xl mb-2">Si te gustó esta nota…</p>
          <h2 className="font-utop text-6xl md:text-8xl mb-12">También te va a gustar leer</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((a, i) => (
              <motion.div key={a.slug} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.6 }}>
                <Link to={`${base}/${a.slug}`} data-testid={`relacionada-${a.slug}`} className="group block bg-white text-utop-azul rounded-[2rem] overflow-hidden shadow-xl hover:-translate-y-2 hover:shadow-2xl transition duration-300">
                  <div className="relative aspect-[16/10] overflow-hidden"><img loading="lazy" src={a.portada} alt={a.titulo} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                    <span className="absolute top-4 left-4 bg-utop-mostaza text-utop-azul text-sm font-bold px-3 py-1 rounded-full">{a.categoria}</span></div>
                  <div className="p-6"><h3 className="font-utop text-4xl leading-[0.9] mb-3">{a.titulo}</h3><p className="text-lg text-utop-azul/80 line-clamp-2 mb-4">{a.resumen}</p>
                    <span className="inline-flex items-center gap-2 text-utop-rojo font-semibold text-lg">Leer nota <ArrowRight size={20} className="group-hover:translate-x-1 transition" /></span></div>
                </Link>
              </motion.div>
            ))}
            {items.length < 3 && [
              { to: "/marca/experiencias", t: "Vive la próxima experiencia", d: "Círculos, charlas y salidas culturales cada mes.", c: "bg-utop-mostaza text-utop-azul" },
              { to: "/marca/blog", t: "Muy pronto, más notas", d: "Historias de nuestra comunidad que vale la pena leer con calma.", c: "bg-utop-rojo text-white" },
            ].slice(0, 3 - items.length).map((f, i) => (
              <motion.div key={f.to} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (items.length + i) * 0.12, duration: 0.6 }}>
                <Link to={f.to} data-testid={`relacionada-extra-${i}`} className={`group flex flex-col justify-between h-full min-h-[320px] rounded-[2rem] p-8 ${f.c} shadow-xl hover:-translate-y-2 transition duration-300`}>
                  <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white/25"><Sparkles size={32} /></span>
                  <div><h3 className="font-utop text-5xl leading-[0.9] mb-3">{f.t}</h3><p className="text-lg opacity-85 mb-4">{f.d}</p><span className="inline-flex items-center gap-2 font-semibold text-lg">Descubrir <ArrowRight size={20} className="group-hover:translate-x-1 transition" /></span></div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="relative overflow-hidden bg-cometa-tinta text-cometa-crema" data-testid="notas-relacionadas">
      <LiveBackground tone="dark" />
      <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="font-script text-3xl md:text-4xl text-cometa-dorado mb-2 flex items-center gap-2"><Sparkles size={22} /> Si te gustó esta nota…</p>
            <h2 className="text-4xl md:text-6xl">Sigue con estas</h2>
          </div>
          <Link to={base} className="inline-flex items-center gap-2 text-cometa-celeste font-semibold link-underline">Todos los artículos <ArrowRight size={16} /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((a, i) => {
            const c = colorMap[a.color];
            return (
              <motion.div key={a.slug} initial={{ opacity: 0, y: 40, rotate: -1 }} whileInView={{ opacity: 1, y: 0, rotate: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.6 }}>
                <Link to={`${base}/${a.slug}`} data-testid={`relacionada-${a.slug}`} className={`group relative block rounded-[2rem] p-7 min-h-[300px] overflow-hidden ${c.bg} ${c.fg} hover:-translate-y-2 hover:rotate-1 transition duration-300 shadow-xl`}>
                  {a.portada && <img loading="lazy" src={a.portada} alt="" className="absolute inset-0 w-full h-full object-cover grayscale opacity-30 group-hover:opacity-45 group-hover:scale-105 transition duration-700" />}
                  <div className="relative flex flex-col h-full min-h-[250px]">
                    <span className="text-[11px] tracking-[0.25em] font-bold uppercase opacity-90">{a.categoria}{a.categoria === actual.categoria && " · misma serie"}</span>
                    <h3 className="text-2xl md:text-3xl leading-tight mt-5 mb-3 font-display">{a.titulo}</h3>
                    <p className="opacity-85 text-sm line-clamp-2">{a.resumen}</p>
                    <span className="mt-auto pt-6 inline-flex items-center gap-2 font-semibold text-sm">Leer <ArrowRight size={16} className="group-hover:translate-x-1 transition" /></span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const UtopBlobs = () => (
  <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
    {[["#CFAB04", 0.25, "10%", "-10%", 360], ["#8CA3B3", 0.35, "70%", "50%", 420], ["#9A2422", 0.18, "40%", "80%", 300]].map(([c, o, l, t, s], i) => (
      <motion.span key={i} className="absolute rounded-full blur-3xl" style={{ background: c, opacity: o, width: s, height: s, left: l, top: t }} animate={{ x: [0, 40, -30, 0], y: [0, -30, 30, 0], scale: [1, 1.1, 0.95, 1] }} transition={{ duration: 20 + i * 5, repeat: Infinity, ease: "easeInOut" }} />
    ))}
  </div>
);
